import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

const saApi = axios.create({ baseURL: (import.meta.env.VITE_API_URL || 'http://localhost:3000') + '/api' });

saApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('saAccessToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

saApi.interceptors.response.use(
  res => res,
  err => {
    if (err?.response?.status === 401) {
      console.warn('[SA] 401 Unauthorized — clearing SA session and redirecting to login');
      localStorage.removeItem('saAccessToken');
      localStorage.removeItem('saRefreshToken');
      localStorage.removeItem('saAdmin');
      localStorage.removeItem('saPermissions');
      window.location.href = '/super-admin/login';
    }
    return Promise.reject(err);
  },
);

interface SuperAdmin {
  id: string;
  email: string;
  name: string;
  role: 'SUPER_ADMIN';
}

export type SaPermissionMap = Record<string, Partial<Record<'view' | 'create' | 'edit' | 'delete' | 'export', boolean>>>;

interface SuperAdminAuthContextType {
  admin: SuperAdmin | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // PlatformRole permissions for the logged-in admin (null until loaded).
  permissions: SaPermissionMap | null;
  // True when the admin holds the canonical "Super Admin" role — sidebar
  // should treat them as see-everything regardless of permission map.
  isSuper: boolean;
  permissionsLoaded: boolean;
  refreshPermissions: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const SuperAdminAuthContext = createContext<SuperAdminAuthContextType | null>(null);

export { saApi };

const PERMS_CACHE_KEY = 'saPermissions';
const SUPER_CACHE_KEY = 'saIsSuper';

export function SuperAdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<SuperAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState<SaPermissionMap | null>(null);
  const [isSuper, setIsSuper] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('saAccessToken');
    const stored = localStorage.getItem('saAdmin');
    if (token && stored) {
      try { setAdmin(JSON.parse(stored)); } catch { localStorage.removeItem('saAdmin'); }
    }
    // Warm cached permissions so the sidebar paints correctly on refresh.
    const cachedPerms = localStorage.getItem(PERMS_CACHE_KEY);
    const cachedSuper = localStorage.getItem(SUPER_CACHE_KEY);
    if (cachedPerms) {
      try { setPermissions(JSON.parse(cachedPerms)); setPermissionsLoaded(true); }
      catch { localStorage.removeItem(PERMS_CACHE_KEY); }
    }
    if (cachedSuper === '1') setIsSuper(true);
    setIsLoading(false);
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const { data } = await saApi.get('/super-admin/me/permissions');
      const perms = (data?.permissions ?? null) as SaPermissionMap | null;
      const sup = !!data?.isSuper;
      setPermissions(perms);
      setIsSuper(sup);
      setPermissionsLoaded(true);
      if (perms) localStorage.setItem(PERMS_CACHE_KEY, JSON.stringify(perms));
      else localStorage.removeItem(PERMS_CACHE_KEY);
      if (sup) localStorage.setItem(SUPER_CACHE_KEY, '1');
      else localStorage.removeItem(SUPER_CACHE_KEY);
    } catch {
      // Leave previous state in place — interceptor handles 401.
      setPermissionsLoaded(true);
    }
  }, []);

  // Refresh permissions whenever the admin identity changes (login,
  // logout, or initial hydration from localStorage).
  useEffect(() => {
    if (admin) {
      refreshPermissions();
    } else {
      setPermissions(null);
      setIsSuper(false);
      setPermissionsLoaded(false);
      localStorage.removeItem(PERMS_CACHE_KEY);
      localStorage.removeItem(SUPER_CACHE_KEY);
    }
  }, [admin, refreshPermissions]);

  const login = async (email: string, password: string) => {
    const { data } = await saApi.post('/super-admin/auth/login', { email, password });
    localStorage.setItem('saAccessToken', data.accessToken);
    localStorage.setItem('saRefreshToken', data.refreshToken);
    localStorage.setItem('saAdmin', JSON.stringify(data.admin));

    // Backend bundles permissions + isSuper into the login response so
    // the sidebar paints correctly on first render without a second
    // round-trip. Hydrate state + cache directly from it.
    const map = (data?.permissions ?? null) as SaPermissionMap | null;
    const sup = !!data?.isSuper;
    setPermissions(map);
    setIsSuper(sup);
    setPermissionsLoaded(true);
    if (map) localStorage.setItem(PERMS_CACHE_KEY, JSON.stringify(map));
    else localStorage.removeItem(PERMS_CACHE_KEY);
    if (sup) localStorage.setItem(SUPER_CACHE_KEY, '1');
    else localStorage.removeItem(SUPER_CACHE_KEY);

    setAdmin(data.admin);
  };

  const logout = () => {
    localStorage.removeItem('saAccessToken');
    localStorage.removeItem('saRefreshToken');
    localStorage.removeItem('saAdmin');
    localStorage.removeItem(PERMS_CACHE_KEY);
    localStorage.removeItem(SUPER_CACHE_KEY);
    setAdmin(null);
    setPermissions(null);
    setIsSuper(false);
    setPermissionsLoaded(false);
  };

  return (
    <SuperAdminAuthContext.Provider value={{
      admin,
      isLoading,
      isAuthenticated: !!admin,
      permissions,
      isSuper,
      permissionsLoaded,
      refreshPermissions,
      login,
      logout,
    }}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
}

export function useSuperAdminAuth() {
  const ctx = useContext(SuperAdminAuthContext);
  if (!ctx) throw new Error('useSuperAdminAuth must be used within SuperAdminAuthProvider');
  return ctx;
}
